/**
 * Décoration native du tag `<context>` si un innerHTML le laisse passer.
 * L'historique parse le XML **avant** [innerHTML] (kng-prompt-context + sl-tag)
 * : Angular strippe `<context>`/`<join>` (pas de trait d'union).
 *
 * Format serveur (agent.prompt-attachments.ts) :
 *   <context>
 *     <join type="file" id="…" slug="…" nom="…">…</join>
 *     <join type="email" id="…" mailbox="…">…</join>
 *     <join type="sharepoint" id="…" pageId="…" title="…" webUrl="…">…</join>
 *     <join type="table" id="…">4 cols × 12 lignes, sans en-tête</join>
 *     <join type="image" id="…" nom="…">Image · …</join>
 *     <selection origin="history" source="…">…texte…</selection>
 *   </context>
 */

import { ContextItem } from './kng-model.assistant';
import { contextItemFromJoin } from './kng-prompt-context';

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

function renderChip(item: ContextItem): string {
  const inner = `<sl-icon slot="prefix" name="${item.icon}"></sl-icon>${escapeHtml(item.title)}`;
  const attrs = `class="context-chip" size="small" data-kind="${item.kind}" title="${escapeHtml(item.title)}"`;
  if (item.href) {
    return `<a class="context-chip-link" href="${escapeHtml(item.href)}" target="_blank" rel="noopener"><sl-tag ${attrs}>${inner}</sl-tag></a>`;
  }
  return `<sl-tag ${attrs}>${inner}</sl-tag>`;
}

function enhanceContext(el: HTMLElement): void {
  if (el.dataset['enhanced'] === 'true') return;
  el.dataset['enhanced'] = 'true';

  const items = Array.from(el.querySelectorAll('join, selection'))
    .map(contextItemFromJoin)
    .filter((item): item is ContextItem => item !== null);

  if (!items.length) {
    el.hidden = true;
    return;
  }

  el.innerHTML = items.map(renderChip).join('');
}

let registered = false;

export function registerContextElement(): void {
  if (registered || typeof window === 'undefined' || typeof document === 'undefined') return;
  registered = true;

  const scan = (node: ParentNode) => {
    node.querySelectorAll?.('context').forEach(el => enhanceContext(el as HTMLElement));
  };

  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach(node => {
        if (!(node instanceof HTMLElement)) return;
        if (node.tagName === 'CONTEXT') enhanceContext(node);
        else scan(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
  scan(document);
}
